import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-employment.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { globalStyle } from '../../../global-styles.style.js';
import './employment-active-toggle/employment-active-toggle.component.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import { stratum } from '../../../../constants/stratum.js';
import {
  MatchingStateEnum,
  offerStateEnum,
} from '../../model/customer-offer.model.js';

class RefiStepEmploymentComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepEmployment');
    this.validity = {};
    this.employmentMissing = false;
    this.employmentActiveToggleId = 'employment-active-toggle-input';
  }

  static properties = {
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    employmentStatus: { type: String, reflect: true },
    employedOrSelfEmployed: { type: Boolean, reflect: true },
  };

  static styles = [style, globalStyle];

  get application() {
    return ApplicationService.Application;
  }

  get applicant() {
    return this.isPrimary
      ? ApplicationService.Application.primaryApplicant
      : ApplicationService.Application.coApplicant;
  }

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.employmentStatus = this.applicant.employmentStatus;
    this.employedOrSelfEmployed =
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED ||
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED;
    stratum.publishTag(
      this.isPrimary
        ? TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_EMPLOYMENT
        : TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_CO_APP_EMPLOYMENT
    );
  }

  render() {
    return html`

            <form id="step-employment-form"
                  class="step-employment-form"
                  @submit="${this.onSubmit}"
                  novalidate
            >
                <refi-heading ?hidden="${
                  this.customerOffer &&
                  this.customerOffer.offerState === offerStateEnum.matched &&
                  this.matchingState === MatchingStateEnum.primarySso
                }">
                    ${
                      this.isPrimary
                        ? html`What’s your employment status?`
                        : html`What’s ${this.applicant.firstName}’s employment
                          status?`
                    }

                  <fs-modal-dialog
                      accessible-title="employmentModal"
                      dismissible
                      size="small"
                      id="employmentModal"
                      slot="sub-header"
                  >
                    <fs-button
                        variant="primary"
                        type="button"
                        compact
                        ghost
                        id="open"
                        @click="${() => {
                          stratum.publishTag(
                            TagKey.PERSONAL_EMPLOYMENT_ABOUT_EMPLOYMENT_STATUS_MODAL
                          );
                        }}"
                    >About employment status
                    </fs-button>
                    <refi-heading slot="modal-dialog-header">
                      About employment status
                    </refi-heading>
                    <section class="fs-body-text fs-body-text--normal" slot="modal-dialog-body">
                      <fs-body-text size="normal" variant="primary">Employed means you have taxes withheld on
                        your pay stubs and receive a W-2 at the end of the year.
                      </fs-body-text>
                      <fs-body-text size="normal" variant="primary">Self-Employed means you own your own
                        business or corporation.
                      </fs-body-text>
                      <fs-body-text size="normal" variant="primary">1099 Contractor means you are a self
                        employed independent contractor who does not have taxes withheld on your pay stubs and
                        you receive a 1099 tax form at the end of the year.
                      </fs-body-text>
                    </section>
                    <fs-modal-dialog-footer slot="modal-dialog-footer">
                      <fs-button variant="primary" type="button" class="fs-button fs-button--primary" @click="${
                        this.closeModal
                      }">
                        Okay
                      </fs-button>
                    </fs-modal-dialog-footer>
                  </fs-modal-dialog>
                </refi-heading>

                <div role="radiogroup" aria-labelledby="toggleEmploymentEmployed">
                    <div>
                        <fs-radio id="toggleEmploymentEmployed"
                                  name="toggleEmploymentEmployed"
                                  value="${
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .EMPLOYED
                                  }"
                                  required
                                  ?checked="${
                                    this.employmentStatus ===
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .EMPLOYED
                                  }"
                                  @change="${this.employmentChange}"
                        >Employed
                        </fs-radio>
                    </div>
                    <div>
                        <fs-radio id="toggleEmploymentUnemployed"
                                  name="toggleEmploymentUnemployed"
                                  value="${
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .UNEMPLOYED
                                  }"
                                  required
                                  ?checked="${
                                    this.employmentStatus ===
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .UNEMPLOYED
                                  }"
                                  @change="${this.employmentChange}"
                        >Unemployed
                        </fs-radio>
                    </div>
                    <div>
                        <fs-radio id="toggleEmploymentSelfEmployed"
                                  name="toggleEmploymentSelfEmployed"
                                  value="${
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .SELF_EMPLOYED
                                  }"
                                  required
                                  ?checked="${
                                    this.employmentStatus ===
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .SELF_EMPLOYED
                                  }"
                                  @change="${this.employmentChange}"
                        >Self-Employed/1099 contractor
                        </fs-radio>
                    </div>
                    <div>
                        <fs-radio id="toggleEmploymentRetired"
                                  name="toggleEmploymentRetired"
                                  value="${
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .RETIRED
                                  }"
                                  required
                                  ?checked="${
                                    this.employmentStatus ===
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS
                                      .RETIRED
                                  }"
                                  @change="${this.employmentChange}"
                        >Retired
                        </fs-radio>
                    </div>
                    <div>
                        <fs-radio id="toggleEmploymentOther"
                                  name="toggleEmploymentOther"
                                  value="${
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.OTHER
                                  }"
                                  required
                                  ?checked="${
                                    this.employmentStatus ===
                                    REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.OTHER
                                  }"
                                  @change="${this.employmentChange}"
                        >Other
                        </fs-radio>
                    </div>
                    <fs-body-text size="small" variant="feedback-negative"
                                  ?hidden="${!this.employmentMissing}"
                                  id="employmentActiveInvalid">
                        Please select an employment status.
                    </fs-body-text>
                </div>
                </div>
                ${
                  this.employedOrSelfEmployed
                    ? html` <refi-personal-employment-active-toggle
                        id="${this.employmentActiveToggleId}"
                        @validity="${this.updateValidity}"
                        ?is-primary="${this.isPrimary}"
                      ></refi-personal-employment-active-toggle>`
                    : nothing
                }

                <div class="button-container">
                    <fs-button class="fs-button next-button" variant="primary">Next</fs-button>
                </div>
            </form>
        `;
  }

  employmentChange(ev) {
    this.employmentStatus = ev.target.value;
    stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
      replacements: {
        value: `${this.coAppPrefix}employmentStatus`,
      },
    });
    this.employedOrSelfEmployed =
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED ||
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED;
    this.employmentMissing = false;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  @validateForm()
  async onSubmit() {
    this.employmentMissing =
      this.employmentStatus === null || this.employmentStatus === '';
    this.updateValidity({
      detail: { name: 'employment', isValid: !this.employmentMissing },
    });
    this.requestUpdate();

    let valid = true;
    this.debug.log(this.validity);
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });

    if (valid) {
      // Store old state for tracking changes
      this.oldApplicantInfo = structuredClone(this.applicant);
      this.oldApplicantEmploymentInfo = structuredClone(
        this.applicant.employmentInfo
      );

      const updatedApplication = structuredClone(this.application);
      const applicantType = this.isPrimary ? 'primaryApplicant' : 'coApplicant';

      // Update employment status and employer name
      updatedApplication[applicantType].employmentStatus =
        this.employmentStatus;
      updatedApplication[applicantType].employmentInfo.employerName =
        this.employmentStatus;

      // Update income verification if applicable
      const activeIncomeVerification = this.shadowRoot.getElementById(
        this.employmentActiveToggleId
      )?.values.activeIncomeVerification;

      updatedApplication[
        applicantType
      ].employmentInfo.activeIncomeVerification = this.employedOrSelfEmployed
        ? activeIncomeVerification
        : null;

      // Publish tracking tags
      stratum.publishTag(TagKey.PERSONAL_EMPLOYMENT_EMPLOYMENT_STATUS, {
        replacements: { value: this.employmentStatus },
      });
      stratum.publishTag(TagKey.PERSONAL_EMPLOYMENT_PFCI_QUESTION, {
        replacements: {
          value: activeIncomeVerification,
        },
      });

      // Update employment details based on status
      if (
        ['employed', 'selfemployed', 'other'].includes(this.employmentStatus)
      ) {
        updatedApplication[applicantType].employmentInfo.yearsEmployed = '0';
        updatedApplication[applicantType].employmentInfo.monthsEmployed = '1';
        if (this.employmentStatus === 'other') {
          updatedApplication[applicantType].employmentInfo.jobDescription =
            'Other';
        }
      } else if (this.employmentStatus === 'retired') {
        updatedApplication[applicantType].employmentInfo.yearsEmployed = '0';
        updatedApplication[applicantType].employmentInfo.monthsEmployed = '0';
        updatedApplication[applicantType].employmentInfo.jobDescription =
          'Retired';
      } else if (this.employmentStatus === 'unemployed') {
        updatedApplication[applicantType].employmentInfo.yearsEmployed = null;
        updatedApplication[applicantType].employmentInfo.monthsEmployed = null;
        updatedApplication[applicantType].employmentInfo.jobDescription =
          'Unemployed';
      }

      // Update application state
      ApplicationService.Application = updatedApplication;

      this.tagFieldChangesOnSubmit();
      this.debug.log('valid', this.application);
      this.dispatchEvent(new CustomEvent('next-step'));
    }
  }

  tagFieldChangesOnSubmit() {
    Object.keys(this.applicant).forEach((field) => {
      if (
        typeof this.applicant[field] !== 'object' &&
        this.applicant[field] !== this.oldApplicantInfo[field]
      ) {
        this.debug.log(`${this.coAppPrefix}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.coAppPrefix}${field}`,
          },
        });
      }
    });
    Object.keys(this.applicant.employmentInfo).forEach((field) => {
      if (
        this.applicant.employmentInfo[field] !==
        this.oldApplicantEmploymentInfo[field]
      ) {
        this.debug.log(`${this.coAppPrefix}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.coAppPrefix}${field}`,
          },
        });
      }
    });
  }

  closeModal() {
    this.shadowRoot.getElementById('employmentModal').closeModalDialog();
  }
}

customElements.define('refi-step-employment', RefiStepEmploymentComponent);
